/**
 * snotes-ai(aiStudio) 화면 AI API 클라이언트. (HTTP 계약 전용 — UI 상태를 다루지 않는다)
 *
 * 업무별 API가 나뉘어 있던 기존 aiEditor 계약과 달리, AI 관련 요청은 단일 엔드포인트로
 * 모인다. 화면 설계 / Query 설정 / 임시저장 / 배포는 모두 Agent가 Tool을 호출해 처리하므로
 * 프론트는 사용자의 자연어 명령을 전달하고 돌아온 draft를 화면에 적용하는 역할만 한다.
 *
 *   사용자 자연어 → POST /snotes/aiStudio → snotes-ai Agent → Agent Tool
 *                                                            ├── 화면 설계
 *                                                            ├── Query 설정
 *                                                            ├── 임시저장
 *                                                            └── 배포
 *
 * POST /snotes/aiStudio  (backend StudioAiProxyController @PostMapping("aiStudio"))
 *   Request : {
 *     command: string (필수),
 *     conversationId?: string,  // 1턴 생략, 2턴부터는 직전 응답 값을 그대로 회신
 *     noIssue?: string,         // 작업 대상 이슈번호 — 저장·배포 단계에 필요
 *     cdEmp?: string            // 담당자사번 — 저장·배포에 필요하므로 첫 턴부터 보낸다
 *   }
 *   Response: ApiResponse<{
 *     message, conversationId, toolTrace[], iterations, totalTokens,
 *     draft: StudioSpec | null, draftQuery, draftQueries
 *   }>
 *
 * - 소스 화면 지정(sourceCdMenu/sourceName)이나 새 화면 이름(targetName)은 계약에 없다.
 *   "발주현황 참고해서 이슈등록 화면 만들어줘"처럼 command 안에 담아 보낸다.
 * - draft가 null이면 잡담/질문 답변이므로 message만 표시하고 현재 화면을 유지한다.
 * - draft는 화면 복제 형태 하나뿐이다(nmMenu/forms/grids = sys_formctrl/sys_formgridcolumn
 *   행, camelCase). 예전의 신규 생성형(title/searchFields/columns)은 더 이상 오지 않는다.
 * - 조회쿼리는 draftQueries(그리드별 SQL 초안, key = cd_grid)로 온다. draftQuery는
 *   grid1 값을 담은 하위 호환 필드이므로, draftQueries가 비어 있으면 grid1로 흡수한다.
 *   (2026-08-28 현재 backend StudioAiProxyResponse에는 draftQuery만 선언되어 있어
 *    실제로는 이 폴백 경로로 grid1 SQL이 들어온다)
 *
 * apiFetch가 CONTEXT_PATH("/snotes")를 자동으로 붙이고 ApiResponse<T>를 반환한다.
 */
import { apiFetch } from "@shared/api/api";
import type { StudioModel, SearchField, GridColumn } from "./studioTypes";

export const AI_STUDIO_ENDPOINT = "/aiStudio";

/** draft의 sys_formctrl 행 (Jackson camelCase, 어댑터가 쓰는 필드만 명시) */
export interface SpecFormControl {
  cdMenu?: string | null;
  nmLabel?: string | null;
  tpCtrl?: string | null;
  cdParam?: string | null;
  fgVisible?: string | null;
  noSeq?: number | null;
  qtRowindex?: number | null;
  qtColindex?: number | null;
  [k: string]: unknown;
}

/** 폼 하나 = cd_form 그룹 하나. cdForm: search | freeform1 | freeform2 | freeform3
 *  (freeform은 그리드 행의 상세 영역이다) */
export interface SpecForm {
  cdForm?: string | null;
  controls?: SpecFormControl[] | null;
}

/** draft의 sys_formgridcolumn 행 */
export interface SpecGridColumn {
  cdMenu?: string | null;
  cdColumn?: string | null;
  nmColumn?: string | null;
  tpColumn?: string | null;
  qtWidth?: number | null;
  fgVisible?: string | null;
  noSeq?: number | null;
  [k: string]: unknown;
}

/** 그리드 하나 = cd_grid 그룹 하나. cdGrid: grid1 | grid2 | sheet1 | sheetref1 */
export interface SpecGrid {
  cdGrid?: string | null;
  columns?: SpecGridColumn[] | null;
}

/**
 * snotes-ai StudioSpec — AI가 만든 화면 1개의 전체 구성.
 * 필드명이 늘어날 수 있어 느슨하게 두고 어댑터에서 필요한 것만 읽는다.
 */
export interface StudioSpec {
  nmMenu?: string | null;
  forms?: SpecForm[] | null;
  grids?: SpecGrid[] | null;
  [k: string]: unknown;
}

/** 그리드별 조회쿼리 초안 — key = cd_grid(grid1, grid2 …), value = SQL */
export type DraftQueries = Record<string, string>;

/** askStudioAi 요청 (backend StudioAiProxyRequest와 1:1) */
export interface AiAskOptions {
  /** 1턴이면 생략. 2턴부터 직전 응답 값을 그대로 회신한다 */
  conversationId?: string;
  /** 작업 대상 이슈번호 — 저장·배포 단계에 필요 */
  noIssue?: string;
  /** 담당자사번 — 저장·배포에 필요하므로 첫 턴부터 보낸다 */
  cdEmp?: string;
}

/** 요청 1회 결과 — 답변 텍스트 + (화면이 왔으면) 화면 모델/초안 */
export interface AiAskResult {
  /** 멀티턴 대화 ID. 다음 askStudioAi 요청에 그대로 회신할 것 */
  conversationId: string | null;
  message: string;
  /** 미리보기용으로 정규화한 화면 모델 (draft에 그릴 내용이 없으면 null) */
  model: StudioModel | null;
  hasScreen: boolean;
  /** 서버가 내려준 화면 초안 원본 — 미리보기 메타 변환에 그대로 쓴다 */
  draft: StudioSpec | null;
  /** 그리드별 조회쿼리 초안 (draftQuery만 온 응답은 grid1로 흡수) */
  draftQueries: DraftQueries;
  /** Agent가 호출한 Tool 순서. 비어 있으면 DB를 보지 않고 답했다는 뜻이다 */
  toolTrace: string[];
  iterations: number;
  totalTokens: number;
}

/** 백엔드 aiStudio 응답 data (StudioAiProxyResponse와 1:1) */
interface StudioAiData {
  message?: string;
  conversationId?: string;
  toolTrace?: string[];
  iterations?: number;
  totalTokens?: number;
  draft?: StudioSpec | null;
  /** grid1 조회쿼리 (하위 호환 필드) */
  draftQuery?: string | null;
  /** 그리드별 조회쿼리 초안 */
  draftQueries?: Record<string, string | null> | null;
}

/**
 * AI에게 요청 (대화 + 화면설계 + 저장/배포 겸용 — 무엇을 할지는 Agent가 정한다).
 * 답변 텍스트와 이번 턴의 화면 초안(있으면)을 함께 반환한다.
 */
export async function askStudioAi(
  command: string,
  options?: AiAskOptions
): Promise<AiAskResult> {
  const body: Record<string, unknown> = { command };
  if (options?.conversationId) body.conversationId = options.conversationId;
  if (options?.noIssue) body.noIssue = options.noIssue;
  if (options?.cdEmp) body.cdEmp = options.cdEmp;

  const res = await apiFetch<StudioAiData>(AI_STUDIO_ENDPOINT, {
    method: "POST",
    body: body as unknown as BodyInit,
  });
  const data = res.data ?? {};
  const draft = data.draft ?? null;
  const hasScreen = specHasContent(draft);
  const model = hasScreen && draft ? specToStudioModel(draft) : null;

  return {
    conversationId: nonEmpty(data.conversationId),
    message: pickMessage(data.message ?? res.message, model),
    model,
    hasScreen,
    draft,
    draftQueries: pickDraftQueries(data),
    toolTrace: Array.isArray(data.toolTrace) ? data.toolTrace : [],
    iterations: typeof data.iterations === "number" ? data.iterations : 0,
    totalTokens: typeof data.totalTokens === "number" ? data.totalTokens : 0,
  };
}

/** 문자열이면서 내용이 있을 때만 값으로 인정 (공백/빈문자/null → null) */
function nonEmpty(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/**
 * 조회쿼리 정규화 — draftQueries가 정석이고, draftQuery는 grid1을 담은 하위 호환 필드다.
 * 둘 다 오면 draftQueries를 믿는다(그리드별로 나뉘어 있는 쪽이 더 자세하다).
 * 프론트에서 없는 그리드 키를 만들어 넣지 않는다 — 서버가 준 것만 담는다.
 */
function pickDraftQueries(data: StudioAiData): DraftQueries {
  const out: DraftQueries = {};
  const rows = data.draftQueries;
  if (rows && typeof rows === "object") {
    Object.keys(rows).forEach((key) => {
      const sql = nonEmpty(rows[key]);
      if (sql) out[key] = sql;
    });
  }
  if (!out.grid1) {
    const grid1 = nonEmpty(data.draftQuery);
    if (grid1) out.grid1 = grid1;
  }
  return out;
}

/**
 * draft에 실제로 그릴 수 있는 화면 구성요소가 들어있는지 판별.
 *
 * nmMenu만 있는 경우는 화면으로 치지 않는다 — 예전에는 이때도 true를 주고 부족한 부분을
 * 로컬 mock 생성기로 채웠는데, 그러면 AI가 만들지 않은 가짜 컬럼과 가짜 샘플 데이터가
 * 실제 화면처럼 그려져 사용자가 그대로 배포해버린다.
 * 구성요소가 없으면 화면을 적용하지 않고 message만 보여주는 게 맞다.
 */
function specHasContent(spec: StudioSpec | null): boolean {
  if (!spec) return false;
  return Boolean(
    spec.forms?.some((f) => f?.controls?.length) ||
      spec.grids?.some((g) => g?.columns?.length)
  );
}

/** 응답에서 사용자에게 보여줄 답변 텍스트 결정 (data.message가 1순위) */
function pickMessage(
  primaryMessage: string | undefined,
  model: StudioModel | null
): string {
  const primary = (primaryMessage || "").trim();
  if (primary && !/^(success|ok|성공)$/i.test(primary)) return primary;

  if (model) {
    return `"${model.title}" 화면을 편집기에 반영했어요. 마음에 들면 "저장해줘"라고 말씀해 주세요.`;
  }

  return "답변 내용이 비어 있어요. 만들고 싶은 화면을 조금 더 구체적으로 설명해 주세요. (예: 발주현황을 참고해서 이슈등록 화면 만들어줘)";
}

/* ── StudioSpec → StudioModel 어댑터 ──
 * draft는 화면 복제 형태(forms/grids = sys_formctrl/sys_formgridcolumn 행) 하나뿐이다.
 */

/** 입력 필드가 아닌 레이아웃/버튼성 컨트롤 — 편집기 검색조건에서 제외 */
const NON_FIELD_CTRLS = new Set([
  "SnLabel",
  "SnGroupLabel",
  "SnButton",
  "SnTab",
  "SnEmpty",
]);

/** noSeq → qtRowindex → qtColindex 순 오름차순 (null은 뒤로) */
function bySeq(a: SpecFormControl, b: SpecFormControl): number {
  const n = (v: number | null | undefined) =>
    typeof v === "number" ? v : Number.MAX_SAFE_INTEGER;
  return (
    n(a.noSeq) - n(b.noSeq) ||
    n(a.qtRowindex) - n(b.qtRowindex) ||
    n(a.qtColindex) - n(b.qtColindex)
  );
}

/**
 * draft의 검색 컨트롤을 SearchField로 평탄화.
 *
 * type을 tp_ctrl에서 되살리지 않고 "text"로 고정하는 이유:
 * 미리보기는 specToPreviewMeta가 원본 tp_ctrl을 그대로 써서 그리므로(previewMeta.ts),
 * 이 함수의 결과는 렌더에 쓰이지 않는다. 실제로 쓰이는 건 개수뿐이다.
 * (편집기 SearchField.type은 text/date/daterange/select 4종뿐이라, 굳이 매핑해봐야
 *  SnRadioBox·SnMultiCodeBox가 select로 뭉개진 뒤 SnComboBox로 되돌아오는 손실만 남는다)
 */
function cloneFormsToSearchFields(
  forms: SpecForm[] | null | undefined
): SearchField[] {
  const out: SearchField[] = [];
  (forms ?? []).forEach((form) => {
    (form?.controls ?? [])
      .filter(
        (c) => c && c.fgVisible !== "N" && !NON_FIELD_CTRLS.has(c.tpCtrl ?? "")
      )
      .sort(bySeq)
      .forEach((c) => {
        out.push({
          label: (c.nmLabel ?? "").trim() || `조건${out.length + 1}`,
          type: "text",
          param: (c.cdParam ?? "").trim() || undefined,
        });
      });
  });
  return out;
}

function cloneGridsToColumns(
  grids: SpecGrid[] | null | undefined
): GridColumn[] {
  const out: GridColumn[] = [];
  (grids ?? []).forEach((grid) => {
    (grid?.columns ?? [])
      .filter((c) => c && c.fgVisible !== "N")
      .sort((a, b) => {
        const n = (v: number | null | undefined) =>
          typeof v === "number" ? v : Number.MAX_SAFE_INTEGER;
        return n(a.noSeq) - n(b.noSeq);
      })
      .forEach((c) => {
        out.push({
          key:
            (c.cdColumn ?? "").trim() ||
            (c.nmColumn ?? "").trim() ||
            `col${out.length}`,
          label:
            (c.nmColumn ?? "").trim() ||
            (c.cdColumn ?? "").trim() ||
            `컬럼${out.length + 1}`,
          width:
            typeof c.qtWidth === "number" && c.qtWidth > 0
              ? c.qtWidth
              : undefined,
          align: c.tpColumn === "SnNumberBox" ? "right" : "left",
        });
      });
  });
  return out;
}

/** draft에서 새 화면의 cd_menu 추출 (행마다 스탬프된 cdMenu) */
function cloneCdMenu(spec: StudioSpec): string | undefined {
  for (const form of spec.forms ?? []) {
    for (const c of form?.controls ?? []) {
      if (c?.cdMenu) return c.cdMenu;
    }
  }
  for (const grid of spec.grids ?? []) {
    for (const c of grid?.columns ?? []) {
      if (c?.cdMenu) return c.cdMenu;
    }
  }
  return undefined;
}

/**
 * draft를 그대로 모델로 옮긴다. 비어 있는 부분은 비어 있는 채로 둔다 —
 * 로컬에서 지어낸 컬럼/샘플행을 섞으면 사용자가 그걸 AI 결과로 오인한다.
 * (호출 전 specHasContent로 걸러지므로 완전히 빈 모델이 나오지는 않는다)
 */
function specToStudioModel(spec: StudioSpec): StudioModel {
  return {
    title: (spec.nmMenu ?? "").trim() || "새 화면",
    code: (cloneCdMenu(spec) ?? "").trim(),
    searchFields: cloneFormsToSearchFields(spec.forms),
    columns: cloneGridsToColumns(spec.grids),
  };
}
